import {action, computed, observable, observer, useAsObservableSource, classNames, React, Tooltip2} from '@api'
import {useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {StoresInstance} from '@stores/StoresInstance'
import {SplitterNode, ILayoutCallbacks, RowNode, BorderNode, DragDrop, CLASSES, Orientation, Actions, FlexLayoutNode} from '@flexlayout'
import * as css from './Splitter.css'

/** @hidden @internal */
export interface ISplitterProps {
  layout: ILayoutCallbacks;
  node: SplitterNode;
}

export class SplitterStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: ISplitterProps) {
    super(stores)
  }

  @observable bounds: number[] = []
  @observable.ref outlineDiv: HTMLDivElement

  @computed get parentNode(): RowNode | BorderNode {
    return this.node.parent as RowNode | BorderNode
  }

  @action onMouseDown = (event: Event | React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if ((event as React.MouseEvent<HTMLDivElement>).button === 2) return // Todo - what about the touch event with 2 fingers?

    if (this.disabled) return

    const {flexLayout, node, parentNode, onDragStart, onDragMove, onDragEnd, onDragCancel, onDoubleClick, props: {layout}} = this,
      {rootDiv, currentDocument} = layout
    DragDrop.instance.startDrag(event, onDragStart, onDragMove, onDragEnd, onDragCancel, undefined, onDoubleClick, currentDocument, rootDiv)
    this.bounds = parentNode._getSplitterBounds(node, true)

    this.outlineDiv = currentDocument!.createElement('div')
    const {outlineDiv} = this
    outlineDiv.style.position = 'absolute'
    outlineDiv.className = layout.getClassName(CLASSES.FLEXLAYOUT__SPLITTER_DRAG)
    outlineDiv.style.cursor = node.orientation === Orientation.HORZ ? 'ns-resize' : 'ew-resize'
    node.rect.positionElement(outlineDiv)
    rootDiv.appendChild(outlineDiv)
    flexLayout.draggingNode = node
  }

  @action onDragCancel = (wasDragging: boolean) => {
    const {layout: {rootDiv}, outlineDiv} = this

    rootDiv.removeChild(outlineDiv as Element)
  }

  @action onDragStart = () => {
    if (this.disabled) return false
    return true
  }

  @action onDragMove = (event: React.MouseEvent<Element>) => {
    const {outlineDiv, node, getBoundPosition, layout} = this
    const clientRect = layout.domRect
    const pos = {
      x: event.clientX - clientRect.left,
      y: event.clientY - clientRect.top
    }

    if (outlineDiv) {
      if (node.orientation === Orientation.HORZ) {
        outlineDiv!.style.top = getBoundPosition(pos.y - 4) + 'px'
      } else {
        outlineDiv!.style.left = getBoundPosition(pos.x - 4) + 'px'
      }
    }

    if (layout.realtimeResize) {
      this.updateLayoutThrottled()
    }
  }

  @action updateLayoutThrottled = _.throttle(() => this.updateLayout(), 24)

  @action updateLayout = () => {
    const {outlineDiv, node, parentNode, layout} = this
    if (!outlineDiv.parentElement) return

    let value = 0
    if (outlineDiv) {
      if (node.orientation === Orientation.HORZ) {
        value = outlineDiv!.offsetTop
      } else {
        value = outlineDiv!.offsetLeft
      }
    }

    if (parentNode instanceof BorderNode) {
      const pos = (parentNode as BorderNode)._calculateSplit(node, value)
      layout.doAction(Actions.adjustBorderSplit((node.parent as FlexLayoutNode).id, pos))
    } else {
      const splitSpec = parentNode._calculateSplit(node, value)
      if (splitSpec !== undefined) {
        layout.doAction(Actions.adjustSplit(splitSpec))
      }
    }
  }

  @action onDragEnd = () => {
    this.updateLayout()
    const {outlineDiv, layout} = this
    layout.rootDiv.removeChild(outlineDiv as HTMLDivElement)
  }

  @action onDoubleClick = (e) => {
    const {node, flexLayout} = this
    flexLayout.onDoubleClickSplitter(node)(e)
  }

  @action onContextMenu = (e) => {
    this.flexLayout.onContextMenu_forSplitter(this.node)(e)
  }

  get node() { return this.props.node}

  get layout() { return this.props.layout}

  @computed get className() {
    const {layout, parentNode, node} = this
    const cm = layout.getClassName
    let className = cm(CLASSES.FLEXLAYOUT__SPLITTER) + ' ' + cm(CLASSES.FLEXLAYOUT__SPLITTER_ + node.orientation.name)

    if (parentNode instanceof BorderNode) {
      className += ' ' + cm(CLASSES.FLEXLAYOUT__SPLITTER_BORDER)
    }
    return className
  }

  getBoundPosition = (p: number) => {
    const {bounds} = this
    let rtn = p
    if (p < bounds[0]) {
      rtn = bounds[0]
    }
    if (p > bounds[1]) {
      rtn = bounds[1]
    }

    return rtn
  }

  /**
   * Disable drag resize for sidebar splitters ( L | D1 | D2 | R )   L/R splitters shouldn't be sizable.
   */
  @computed get disabled() {
    const {node, parentNode, parentNode: {drawChildren}, node: {indexInParent}} = this

    return false

    // if (parentNode.orientation.name === 'vert') return false
    //
    // if (indexInParent === 1) {
    //   // Disallow if the left neighbor is a sidebar
    //   return drawChildren[0].id.endsWith('Sidebar')
    // } else if (indexInParent + 2 === drawChildren.length) {
    //   const nodeToMyRight = drawChildren[indexInParent + 1]
    //   return nodeToMyRight.id.endsWith('Sidebar')
    // }

    // return false
  }

  @computed get style() {
    return this.node._styleWithPosition({})
  }
}

/** @hidden @internal */
export const Splitter = observer(function Splitter(props: ISplitterProps) {
  const {layout, node} = props,
    obsProps = useAsObservableSource({layout, node}),
    store = useVipLocalStore((s) => new SplitterStore(s, obsProps)),
    {onMouseDown, onDoubleClick, onContextMenu, className, disabled, style} = store

  return (
    <div
      data-disabled={disabled}
      data-orientation={node.orientation.name}
      onTouchStart={onMouseDown}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      style={style}
      className={classNames(className, css.root)}
      onContextMenu={onContextMenu}/>)
})

/*
<Tooltip2 content='Right-Click for More'
                   position={node.isVertical ? 'right' : 'bottom'}
        >
 */


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/view/Splitter.tsx